
import {Button, Checkbox, Grid, Icon, Input, Label, Select, TextArea} from "semantic-ui-react";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {BuilderContext, registerWidgetType} from "../WidgetRegistry";
import {
  DateFinancialValueTypes,
  FinancialValueType,
  FinancialValueTypes
} from "../../../ps-models";
import {formattedValueAsNumber, formatValueWithValueType} from "../../../ps-models/formatting";
import {
  getConfig,
  getGlobalContext,
  getStore,
  useOnStoreReady,
  useUpdateOnGlobalContextChange
} from "./commons";
import {StrictButtonProps} from "semantic-ui-react/dist/commonjs/elements/Button/Button";
import {ValueType} from "../../../ps-models/line-items";
import {SingleDatePicker} from "../../../lineitems-store/DateRange";
import {LineItemsStore} from "../../../ps-models/lineitems-store";

interface ConfigProps {
  label: string,
  type: FinancialValueType,
  addToStore?: boolean,
}

registerWidgetType({
    typeId: 'Input',
    metadata: {
      name: 'Simple Input Widget',
      description: '',
      icon: 'terminal',
    },
    defaultConfig: {
      label: 'Param',
      type: "number",
      addToStore: false,
    },
  renderConfig: (config: any, context: BuilderContext, setConfig: (config: string) => void) => {
    return <WidgetConfig
      config={config} context={context} onConfigChange={setConfig}/>
  },
    render: (config: any, context: BuilderContext, setOutput) => {
      return <WidgetWithCompare config={config.config} context={context} setOutput={setOutput}/>
    }
  }
)

export function WidgetWithCompare({config, context, setOutput}: {config: ConfigProps, context: BuilderContext, setOutput:(key: string, value: any) => void }) {
  useOnStoreReady(context);
  useUpdateOnGlobalContextChange(context);

  const storeToCompareWith = context.appContext.getLastStoreToCompareWith();
  const mainStore = context.appContext.getStore();
  if(storeToCompareWith){
    return <Grid style={{width: '100%'}}>
      <Grid.Row columns={2}>
        <Grid.Column>
          <InputWidget config={config} context={{...context, store: mainStore}} setOutput={setOutput}/>
        </Grid.Column>
        <Grid.Column style={{border: '2px dotted #FF9800'}}>
          <InputWidget config={config} context={{...context, store: storeToCompareWith, isRenderingComparisonStore: true}} setOutput={setOutput}/>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  }

  return <InputWidget
      config={config}
      context={{...context, store: mainStore}} setOutput={setOutput}/>
}


function WidgetConfig({config, context, onConfigChange}:
                                      { config: ConfigProps, context: BuilderContext, onConfigChange: (config: any) => void }) {

  let [localConfig, setLocalConfig] = React.useState(config);

  const handleApply = () => {
    onConfigChange(localConfig);
  }

  return <>
    Label: <Input value={localConfig.label}

                  onChange={(e, data) => {
      setLocalConfig({...localConfig, label: data.value as string})
    } }/>
    Type: <Select options={FinancialValueTypes.map((vt)=>({value: vt, text: vt, key: vt}))}
                  defaultValue={localConfig.type ?? "number"}
                  onChange={(e, data) => {
                    setLocalConfig({...localConfig, type: data.value as FinancialValueType})
                  } }/>
    <Checkbox toggle checked={localConfig?.addToStore}
              onChange={(e, data) => {
                if(!!data.checked === false){
                  // @TODO
                // Should remove the li from store...
                }
                setLocalConfig({...localConfig, addToStore: !!data.checked})
              }}
              label={"Add to store"} />
    <Button onClick={handleApply}>Apply</Button>
  </>

}

function InputWidget({config, setOutput, context}: {
                                   config: ConfigProps,
                                   context: BuilderContext,
                                   setOutput: (key: string, value: any) => void
}){
  const globalContext = getGlobalContext(context);
  let store = getStore(context);

  let initialValue = context?.params?.value;

  if(context.isRenderingComparisonStore && config.label){
    if(globalContext.compareWithParamsMap){
      let val = globalContext.compareWithParamsMap[config.label] ?? 0;
      initialValue = val;
    }
  }
  if(!context.isRenderingComparisonStore && config.label){
    if(globalContext.paramsMap){
      let val = globalContext.paramsMap[config.label];
      initialValue = val;
    }
  }

  const onSave = useCallback((value:string)=>{
    let rawValue = getRawValue(value, config.type);
    console.info("setting the RAW VALUE to", rawValue, typeof rawValue, config.type, value, typeof value);
    if(!context.isRenderingComparisonStore){
      console.info("SETTING THE OUTPUT", rawValue)
      setOutput('value', rawValue);
      if(config.addToStore){
        context.appContext.setParameter(config.label, rawValue, config.type);
      }
    }
  }, [context,config.type])

  if(config.addToStore && initialValue === undefined){
    let valueInStore = store.getParam(config.label);
    initialValue = valueInStore ?? 0;
    if(DateFinancialValueTypes.includes(config.type as any)){
      const currentDate = new Date();
      const beginningOfTodayInMs = currentDate.setHours(0,0,0,0);
      initialValue = (beginningOfTodayInMs > store.timeIndex.startDate.getTime() && beginningOfTodayInMs < store.timeIndex.endDate.getTime()) ? beginningOfTodayInMs : store.timeIndex.startDate.getTime()
    }
  }

  const defaultValue = useMemo(()=>{
    return initialValue;
  }, [])

  return <Widget
      initialValue={initialValue}
      defaultValue={defaultValue}
      label={config.label}
      inputValueType={config.type}
      onSave={onSave}
      readOnly={context.isRenderingComparisonStore ?? context.params.readOnly}
      store={store}
      />
}

function Widget({initialValue, defaultValue, inputValueType, label, onSave, readOnly, store}: {
  initialValue: ValueType,
  defaultValue: ValueType,
  label: string;
  inputValueType: FinancialValueType;
  onSave: (formattedValue: string) => void,
  readOnly: boolean,
  store: LineItemsStore
}) {
  let [formattedValue, setFormattedValue] = useState<string>(formatValueWithValueType(initialValue, inputValueType, -1));

  const handleValueFinalization = ()=>{
    onSave(formattedValue)
  }

  const handleResetDefaultValue = ()=> {
    let fVal = formatValueWithValueType(defaultValue, inputValueType, -1);
    setFormattedValue(fVal);
    onSave(fVal);
  }

  useEffect(()=>{
    console.info("Here with initialValue", initialValue, formatValueWithValueType(initialValue, inputValueType, -1))
    if(initialValue !==undefined){
      setFormattedValue(formatValueWithValueType(initialValue, inputValueType, -1))
      onSave(formatValueWithValueType(initialValue, inputValueType, -1));
    }
  }, [initialValue])
  if(inputValueType === "string") {
    return <>
      <label>{label}</label>
      <TextArea
        key={`${label}${readOnly}`}
        style={{width: '100%'}}
        defaultValue={formattedValue}
        value={formattedValue}
        readOnly={readOnly}
        onChange={(e, {value}) => {
          console.info("setting the output to", value, typeof value);
          setFormattedValue(formatValueWithValueType(value as string, inputValueType, -1))
        }}
        minHeight={2}
    ></TextArea>
      <AcceptButton onClick={handleValueFinalization} disabled={readOnly} />
    </>
  } else if(DateFinancialValueTypes.includes(inputValueType as any)) {
    let initialStartDate = new Date(getRawValue(formattedValue, inputValueType));
    return <div style={{display: "flex", alignItems: "center", width: "100%", gap:0}}>
      <Label size={"large"}>{label}</Label>
      <div>
        <SingleDatePicker
            initialStartDate={initialStartDate}
            minDate={store.timeIndex.startDate}
            maxDate={store.timeIndex.endDate}
            onSelect={(selectedDate)=>{
              let numericValue = selectedDate.getTime();
              setFormattedValue(formatValueWithValueType(numericValue, inputValueType, -1))
            }}
            useUTC
        />
      </div>
      <AcceptButton onClick={handleValueFinalization} disabled={readOnly ?? false} />
    </div>
  } else {
    return <Input
      key={`${label}${readOnly}`}
      label={label}
      defaultValue={formattedValue}
      value={formattedValue}
      readOnly={readOnly}
      onChange={(e, data) => {
        setFormattedValue(formatValueWithValueType(data.value, inputValueType, -1))
      }}
        action={
       <>
         <AcceptButton onClick={handleValueFinalization} disabled={readOnly} />
         <ResetDefaultValueButton onClick={handleResetDefaultValue} disabled={readOnly} />
       </>
    }
    />
  }
}


function AcceptButton({onClick, disabled}: {onClick: StrictButtonProps['onClick'], disabled: boolean}){
  return <Button icon onClick={onClick} disabled={disabled}>
    <Icon color={'green'} name='check' />
  </Button>
}

function ResetDefaultValueButton({onClick, disabled}: { onClick: any, disabled: boolean }) {
  return <Button icon onClick={onClick} disabled={disabled}>
    <Icon color={'blue'} name='undo'/>
  </Button>
}

const getRawValue = (value: string, valueType: FinancialValueType)=>{
  return valueType === "string" ? (value ?? ""): formattedValueAsNumber(value, valueType)
}